import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, Form, Row, Col, Tab, Tabs, Spinner, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { getEntity } from '@lib/api';
import { IcoReported, IcoEdit } from '@icons';
import CustomTooltip from '@components/ag-grid/CustomTooltip';
import UploadDocument from 'components/form/upload/uploadDocuments';
import CheckList from 'components/checklist/checkList';
import { formatToDecimals, dateFormatter } from '@lib/helpers/formatting';
import { columnCentered, columnToRight } from '@lib/helpers/agGridHelpers';
import OESimpleTable from 'components/oetable/OESimpleTable';
import { OETrans } from 'components/translation/OETrans';
import { UserContext } from 'components/contexts/UserProvider';

const InvoiceDetailSummaryModal = ({
	data,
	setRefresh,
	modalIsVisible,
	setModalIsVisible,
	setModalData,
	spinnerVisible,
	isSummary,
	projectStreamConfigurationType,
	projectAdvancedConfig,
	isRenderTask = false,
}) => {
	const { userAccess } = useContext(UserContext);
	const { projectId } = useParams();
	const [detail, setDetail] = useState({});
	const [sbShow, setSbShow] = useState(<></>);
	const [activeKey, setActiveKey] = useState('registry');
	const [isEdited, setIsEdited] = useState(false);
	const [rowData, setRowData] = useState([]);
	const [columnDef, setColumnDef] = useState([]);
	const [columnDefWp, setColumnDefWp] = useState([]);
	//console.log('detail', detail);

	function transformData(positions) {
		const transformedData = [];

		if (positions && Array.isArray(positions)) {
			positions.forEach((position) => {
				if (position && position.quotas && Array.isArray(position.quotas)) {
					position.quotas.forEach((quota) => {
						const newObject = { ...position };
						delete newObject.quotas;

						if (quota) {
							Object.keys(quota).forEach((key) => {
								newObject[key + 'Q'] = quota[key];
							});

							transformedData.push(newObject);
						}
					});
				}
			});
		}

		setRowData(transformedData);
	}

	useEffect(() => {
		if (modalIsVisible && projectId && data.id) {
			getEntity('invoices', projectId, data.id).then((retrievedInvoice) => {
				if (retrievedInvoice.success) {
					setDetail(retrievedInvoice.data);
					transformData(retrievedInvoice.data.positions);
				}
			});
		}
	}, [data, modalIsVisible, projectId]);

	const handleCloseModal = (setShowModal) => {
		if (isEdited) {
			setRefresh(true);
			setIsEdited(false);
		}
		setSbShow(<></>);
		if (setModalData) setModalData([]);
		setShowModal(false);
	};

	const OnlyChecklistCellRenderer = useCallback((params) => {
		//console.log('params', params.queryParam, params);

		const stoplightColor = {
			red: '#C94723',
			yellow: '#D4BE31',
			green: '#689689',
		};

		if (params.value) {
			let circleStyle;
			let message = '';

			circleStyle = {
				display: 'inherit',
				width: '1.2em',
				height: '1.2em',
				borderRadius: '2em',
				MozBorderRadius: '2em',
				WebkitBorderRadius: '2em',
				backgroundColor: stoplightColor[params?.value.color],
				border: `2px solid ${stoplightColor[params?.value.color]}`,
			};

			switch (params?.value.color) {
				case 'yellow':
					message = 'Attenzione';
					break;
				case 'green':
					message = 'Conforme';
					break;
				case 'red':
					message = 'Non conforme';
					break;
				default:
					break;
			}

			return (
				<>
					<Row
						className="align-items-center justify-content-center h-100 flex-nowrap"
						//style={{ justifyContent: params.align ?? "justify-content-between"}}
					>
						<Col className="col-auto">
							<OverlayTrigger placement="top" overlay={<Tooltip>{message}</Tooltip>}>
								<i style={{ ...circleStyle }} />
							</OverlayTrigger>
						</Col>
					</Row>
				</>
			);
		}
	}, []);

	const ReportedCheckRenderer = useCallback((params) => {
		const reported = params?.value;

		const reportedTooltip = (props) => <Tooltip {...props}>Rendicontato</Tooltip>;

		return (
			<>
				{reported && (
					<OverlayTrigger placement="top" overlay={reportedTooltip}>
						<Row className="align-items-center justify-content-center h-100 px-0">
							<Col className="col-auto text-center">
								<IcoReported />
							</Col>
						</Row>
					</OverlayTrigger>
				)}
			</>
		);
	}, []);

	const getValueFilterStoplight = (value) => {
		let string = '';

		switch (value) {
			case 'green':
				string = 'verde';
				break;
			case 'yellow':
				string = 'giallo';
				break;
			case 'red':
				string = 'rosso';
				break;
			case 'white':
				string = 'bianco';
				break;
			default:
				break;
		}

		return string;
	};

	useEffect(() => {
		setColumnDef([
			{
				headerName: 'Posizione',
				field: 'position',
				filter: 'agTextColumnFilter',
				minWidth: 150,
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Classificazione interna',
				field: 'internalTaxonomy',
				tooltipField: 'internalTaxonomy',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return params.data.internalTaxonomy ?? 'N.D.';
				},
				minWidth: 300,
			},
			{
				headerName: 'Descrizione Posizione',
				field: 'positionDescription',
				tooltipField: 'positionDescription',
				filter: 'agTextColumnFilter',
				minWidth: 380,
			},
			{
				headerName: 'Matricola Materiale',
				field: 'code',
				filter: 'agTextColumnFilter',
				minWidth: 220,
				valueGetter: (params) => {
					return params.data.materialQ?.code ?? 'N.D.';
				},
			},
			{
				headerName: 'Etichettabile',
				field: 'labelableQ',
				filter: 'agTextColumnFilter',
				minWidth: 170,
			},
			/* {
			headerName: 'Q.tà Consuntivata',
			field: 'finalQuantity',
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
			minWidth: 250,
		 },
		 {
			headerName: 'Importo Consuntivato €',
			field: 'finalAmount',
			filter: 'agTextColumnFilter',
			valueGetter: (params) => {
			   return formatToDecimals(params.data.finalAmount);
			},
			type: 'rightAligned',
			minWidth: 280,
		 }, */
			{
				headerName: 'Voce di spesa',
				field: 'projectExpenseItemQ.noticeExpenseItem.name',
				filter: 'agTextColumnFilter',
				minWidth: 200,
			},
			{
				headerName: 'Sottovoce di spesa',
				field: 'projectExpenseItemQ.name',
				filter: 'agTextColumnFilter',
				minWidth: 200,
			},
			{
				headerName: 'Quota',
				field: 'quotaNumberQ',
				filter: 'agTextColumnFilter',
				minWidth: 120,
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Importo Quota €',
				field: 'amountQ',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.amountQ);
				},
				type: 'rightAligned',
				minWidth: 200,
			},
			{
				headerName: 'Data Quota',
				field: 'dueDateQ',
				valueGetter: (params) => {
					if (params.data.dueDateQ) {
						return dateFormatter(params.data.dueDateQ);
					} else return;
				},
				filter: 'agTextColumnFilter',
				...columnCentered(),
				minWidth: 200,
			},

			{
				headerName: 'Codice Impianto',
				field: 'plantCode',
				filter: 'agTextColumnFilter',
				minWidth: 220,
				valueGetter: (params) => {
					return params.data.plantQ?.plantCode ?? 'N.D.';
				},
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Tipo Impianto',
				field: 'plantType',
				filter: 'agTextColumnFilter',
				minWidth: 220,
				valueGetter: (params) => {
					return params.data.plantQ?.plantType ?? 'N.D.';
				},
			},
			{
				headerName: 'Denominazione Impianto',
				field: 'plantName',
				filter: 'agTextColumnFilter',
				minWidth: 250,
				valueGetter: (params) => {
					return params.data.plantQ?.plantName ?? 'N.D.';
				},
			},
			{
				headerName: 'Check Pubblicità',
				field: 'advertisingQ',
				cellRenderer: OnlyChecklistCellRenderer,
				cellRendererParams: {
					queryParam: 'advertisingQ',
				},
				minWidth: 190,
				maxWidth: 190,
				filter: 'agTextColumnFilter',
				filterValueGetter: (params) => {
					return getValueFilterStoplight(params.data.advertisingQ);
				},
				comparator: (valueA, valueB) => {
					if (valueA.keyValue === valueB.keyValue) return 0;
					return valueA.keyValue > valueB.keyValue ? 1 : -1;
				},
				...columnCentered(),
			},
			{
				headerName: 'Check DNSH',
				field: 'otplantQ.checkliststatus',
				cellRenderer: OnlyChecklistCellRenderer,
				cellRendererParams: {
					queryParam: 'otplantQ.checkliststatus',
				},
				minWidth: 190,
				maxWidth: 190,
				filter: 'agTextColumnFilter',
				filterValueGetter: (params) => {
					return getValueFilterStoplight(params.data.otplantQ.checkliststatus);
				},
				comparator: (valueA, valueB) => {
					if (valueA.keyValue === valueB.keyValue) return 0;
					return valueA.keyValue > valueB.keyValue ? 1 : -1;
				},
				...columnCentered(),
			},
			{
				headerName: 'Centro di costo',
				field: 'otQ.code',
				filter: 'agTextColumnFilter',
				minWidth: 250,
				valueGetter: (params) => {
					return params.data.otQ?.code ?? 'N.D.';
				},
				cellStyle: { fontWeight: 'bold' },
			},
		]);

		setColumnDefWp([
			{
				headerName: 'R',
				headerTooltip: 'Rendicontato',
				tooltipComponent: CustomTooltip,
				field: 'reportedQ',
				cellRenderer: ReportedCheckRenderer,
				minWidth: 70,
				maxWidth: 70,
				resizable: false,
				...columnCentered(),
			},
			{
				headerName: 'Posizione',
				field: 'position',
				filter: 'agTextColumnFilter',
				minWidth: 150,
				maxWidth: 150,
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Descrizione posizione',
				field: 'assetQ.name',
				filter: 'agTextColumnFilter',
				minWidth: 230,
			},
			{
				headerName: 'Identificativo cespite',
				field: 'assetQ.code',
				filter: 'agTextColumnFilter',
				minWidth: 230,
				maxWidth: 250,
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Costo storico €',
				field: 'assetQ.historicalCost',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.assetQ?.historicalCost);
				},
				minWidth: 190,
				...columnToRight(),
			},
			{
				headerName: 'Coefficiente di ammortamento',
				field: 'assetQ.depreciationRate',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.assetQ?.depreciationRate) + '%';
				},
				minWidth: 300,
				maxWidth: 300,
				...columnToRight(),
			},
			{
				headerName: 'Periodo di ammortamento in mesi',
				field: 'assetQ.depreciationPeriodMonths',
				filter: 'agTextColumnFilter',
				minWidth: 320,
				maxWidth: 320,
				...columnToRight(),
			},
			{
				headerName: 'Costo mensile €',
				field: 'assetQ.monthlyCost',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.assetQ?.monthlyCost);
				},
				minWidth: 190,
				...columnToRight(),
			},
			{
				headerName: 'Tempo di utilizzo totale sul progetto (in mesi)',
				field: 'assetQ.usageTimeMonths',
				filter: 'agTextColumnFilter',
				minWidth: 420,
				maxWidth: 420,
				...columnToRight(),
			},
			{
				headerName: 'Utilizzo sul progetto',
				field: 'assetQ.usagePercentage',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.assetQ?.usagePercentage) + '%';
				},
				minWidth: 210,
				maxWidth: 210,
				cellStyle: { fontWeight: 'bold' },
				...columnToRight(),
			},
			{
				headerName: 'Totale costo rendicontabile €',
				field: 'assetQ.totalAccountableCost',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.assetQ?.totalAccountableCost);
				},
				minWidth: 290,
				...columnToRight(),
			},
			{
				headerName: 'ID Quota',
				field: 'quotaNumberQ',
				filter: 'agTextColumnFilter',
				minWidth: 130,
				cellStyle: { fontWeight: 'bold' },
			},
			{
				headerName: 'Importo Quota €',
				field: 'amountQ',
				filter: 'agTextColumnFilter',
				valueGetter: (params) => {
					return formatToDecimals(params.data.amountQ);
				},
				minWidth: 200,
				...columnToRight(),
			},
			...(projectAdvancedConfig.some((el) => el.name === 'enable_adv' && el.enabled)
				? [
						{
							headerName: 'Check Pubblicità',
							field: 'advertisingQ',
							cellRenderer: OnlyChecklistCellRenderer,
							cellRendererParams: {
								queryParam: 'advertisingQ',
							},
							minWidth: 190,
							maxWidth: 190,
							filter: 'agTextColumnFilter',
							filterValueGetter: (params) => {
								return getValueFilterStoplight(params.data.advertisingQ);
							},
							comparator: (valueA, valueB) => {
								if (valueA.keyValue === valueB.keyValue) return 0;
								return valueA.keyValue > valueB.keyValue ? 1 : -1;
							},
							...columnCentered(),
						},
					]
				: []),
			{
				headerName: 'Centro di costo',
				field: 'otQ',
				filter: 'agTextColumnFilter',
				minWidth: 250,
				valueGetter: (params) => {
					return params.data.otQ?.code ?? 'N.D.';
				},
				cellStyle: { fontWeight: 'bold' },
			},
		]);
	}, [OnlyChecklistCellRenderer, ReportedCheckRenderer, projectAdvancedConfig, projectStreamConfigurationType]);

	const handleShowModal = (setter) => {
		if (isRenderTask) {
			setModalIsVisible(true);
		}
	};

	const editTooltip = (props) => <Tooltip {...props}>Dettaglio</Tooltip>;

	return (
		<div>
			{isRenderTask && (
				<OverlayTrigger placement="top" overlay={editTooltip}>
					<Button variant="link" onClick={() => handleShowModal(setModalIsVisible)}>
						<IcoEdit className="second-color path ico-small edit" />
					</Button>
				</OverlayTrigger>
			)}

			{modalIsVisible && (
				<Modal {...(isRenderTask ? {fullscreen: true} : {})} size="xl" className="modal-with-nav" show={modalIsVisible} onHide={() => handleCloseModal(setModalIsVisible)} centered>
					<Modal.Header closeButton>
						<Modal.Title>Dettaglio Fattura {userAccess.isSuperAdmin ? detail?.id : ''}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{spinnerVisible ? (
							<Row>
								<Col className="d-flex justify-content-center align-items-center">
									<Spinner animation="border" role="status" variant="dark">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</Col>
							</Row>
						) : (
							<Tabs className="mb-4" activeKey={activeKey} onSelect={(k) => setActiveKey(k)} fill>
								{/* Anagrafica */}
								<Tab eventKey="registry" title={'Dati Fattura'}>
									<Row>
										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Nome Fornitore</Form.Label>
											<Form.Text as="p">{detail?.supplierName ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Codice Fornitore</Form.Label>
											<Form.Text as="p">{detail?.supplierCode ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>P.Iva Fornitore</Form.Label>
											<Form.Text as="p">{detail?.supplierVat ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Data Fattura</Form.Label>
											<Form.Text as="p">{detail?.date ? dateFormatter(detail?.date) : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Nr. Fattura</Form.Label>
											<Form.Text as="p">{detail?.number ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>ID Fattura</Form.Label>
											<Form.Text as="p">{detail?.rpCode ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Nr. Contratto Dedicato</Form.Label>
											<Form.Text as="p"> {detail?.contract?.po === false ? detail?.contract?.number : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Nr. Attingimento</Form.Label>
											<Form.Text as="p">{detail?.contract?.po === true ? detail?.contract?.number : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px', overflowWrap: 'break-word' }}>
											<Form.Label>CUP Progetto</Form.Label>
											<Form.Text as="p">{detail?.project?.cup ?? detail?.stream?.project?.cup ?? 'N.D.'}</Form.Text>
										</Form.Group>

										{projectStreamConfigurationType === 'WP' ? null : (
											<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
												<Form.Label>Nr. Procedura</Form.Label>
												<Form.Text as="p">{detail?.tenderCig ? detail?.tenderCig?.tender?.tenderNumber : 'N.D.'}</Form.Text>
											</Form.Group>
										)}

										{projectStreamConfigurationType === 'WP' ? null : (
											<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
												<Form.Label>CIG</Form.Label>
												<Form.Text as="p">{detail?.tenderCig?.cig ?? 'N.D.'}</Form.Text>
											</Form.Group>
										)}

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Nr. Contratto Quadro</Form.Label>
											<Form.Text as="p">
												{detail?.contract?.po === true && detail?.contract?.frameworkContract
													? detail?.contract?.frameworkContract.number
													: 'N.D.'}
											</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Imponibile (€)</Form.Label>
											<Form.Text as="p">{detail?.taxableAmount ? formatToDecimals(detail?.taxableAmount) : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Iva (€)</Form.Label>
											<Form.Text as="p">{detail?.vatAmount ? formatToDecimals(detail?.vatAmount) : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Totale Fattura (€)</Form.Label>
											<Form.Text as="p">{detail?.totalAmount ? formatToDecimals(detail?.totalAmount) : 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Stream</Form.Label>
											<Form.Text as="p">
												{detail?.stream?.streamId ? `${detail?.stream?.streamId} - ${detail?.stream?.name}` : 'N.D.'}
											</Form.Text>
										</Form.Group>

										{projectStreamConfigurationType === 'WP' ? null : (
											<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
												<Form.Label>Classifica Regis</Form.Label>
												<Form.Text as="p">{detail?.regisTaxonomy ?? 'N.D.'}</Form.Text>
											</Form.Group>
										)}

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Classifica interna</Form.Label>
											<Form.Text as="p">{detail?.internalTaxonomy ?? 'N.D.'}</Form.Text>
										</Form.Group>

										<Form.Group as={Col} className="col-4 px-4" style={{ marginBottom: '40px' }}>
											<Form.Label>Fattispecie RSI</Form.Label>
											<Form.Text as="p">{detail?.rdiTypeCode ?? 'N.D.'}</Form.Text>
										</Form.Group>

										{/* 
                              <Form.Group as={Col} className='col-4 px-4' style={{ marginBottom: '40px' }}>
                                 <Form.Label>Ritenuta D'Acconto (€)</Form.Label>
                                 <Form.Text as='p'> {detail?.withholding ? formatToDecimals(detail?.withholding) : 'N.D.'}</Form.Text>
                              </Form.Group>

                              <Form.Group as={Col} className='col-4 px-4' style={{ marginBottom: '40px' }}>
                                 <Form.Label>Contributi Cassa (€)</Form.Label>
                                 <Form.Text as='p'>
                                    {detail?.pensionFundContribution
                                       ? formatToDecimals(detail?.pensionFundContribution)
                                       : 'N.D.'}
                                 </Form.Text>
                              </Form.Group>

                              <Form.Group as={Col} className='col-4 px-4' style={{ marginBottom: '40px' }}>
                                 <Form.Label>Spese di Trasporto (€)</Form.Label>
                                 <Form.Text as='p'>
                                    {detail?.shippingFee ? formatToDecimals(detail?.shippingFee) : 'N.D.'}
                                 </Form.Text>
                              </Form.Group>
                           */}
									</Row>
									{detail?.project?.cupType !== 'I' && (
										<Row className="mb-2rem">
											<h2>Dettaglio Pagamento</h2>

											<Form.Group as={Col} className="col-4 px-4">
												<Form.Label className="mb-3">CUP Pagamento</Form.Label>
												<Form.Text as="p">{detail?.paymentCup ?? 'N.D.'}</Form.Text>
											</Form.Group>

											{projectStreamConfigurationType === 'WP' ? null : (
												<Form.Group as={Col} className="col-4 px-4">
													<Form.Label className="mb-3">CIG Pagamento</Form.Label>
													<Form.Text as="p">{detail?.paymentCig ?? 'N.D.'}</Form.Text>
												</Form.Group>
											)}

											<Form.Group as={Col} className="col-4 px-4">
												<Form.Label className="mb-3">CRO Pagamento</Form.Label>
												<Form.Text as="p">{detail?.payment?.cro ?? 'N.D.'}</Form.Text>
											</Form.Group>
										</Row>
									)}

									{userAccess.isAboveAuditor && (
										<Row className="flex-column">
											<Col>
												<h2>Dettaglio {projectStreamConfigurationType === 'WP' ? 'Cespiti' : 'Posizioni'}</h2>
											</Col>
											<Col>
												{projectStreamConfigurationType === 'STREAM' ? (
													rowData && rowData.length > 0 ? (
														<OESimpleTable
															columnDefs={columnDef}
															rowData={rowData}
															buttonToolbarVisible={false}
															pageSizeId={'payment_invoice_quotas'}
															sortable={true}
															wrapText={false}
															autoHeight={false}
														/>
													) : (
														<Row className="text-center my-5">
															<span>Non sono presenti posizioni.</span>
														</Row>
													)
												) : null}

												{projectStreamConfigurationType === 'WP' ? (
													rowData.every((posit) => posit.assetQ === null) ? (
														<Row className="text-center my-5">
															<span>Non sono presenti cespiti.</span>
														</Row>
													) : (
														<OESimpleTable
															columnDefs={columnDefWp}
															rowData={rowData}
															buttonToolbarVisible={false}
															pageSizeId={'payment_invoice_quotas'}
															sortable={true}
															wrapText={false}
															autoHeight={false}
														/>
													)
												) : null}
											</Col>
										</Row>
									)}

									{sbShow}
								</Tab>

								{/* Documenti */}
								<Tab eventKey="docs" title={<OETrans code="common.documents" fallback={'Documenti'} />}>
									{detail.id && <UploadDocument entity="invoices" entityId={detail.id} externalBorder={true} title="Documenti correlati" />}

									{detail?.paymentId && (
										<UploadDocument
											entity="payments"
											entityId={detail?.paymentId}
											disableDelete={true}
											title="Documentazione Giustificativa di pagamento"
										/>
									)}
								</Tab>

								{/* CheckLists */}
								{userAccess.isAboveAuditor && (
									<Tab eventKey="checklist-invoices" title={'Checklist Fatture'}>
										{detail.id && <CheckList entityType="invoices" entityId={data.id} disabled={detail?.reported || detail?.reporting} />}
									</Tab>
								)}

								{userAccess.isAboveAuditor && (
									<Tab eventKey="checklist-invoicePayments" title={'Checklist Pagamenti'}>
										{detail.payments?.length > 0 ? (
											<CheckList
												entityType="invoicePayments"
												entityId={detail?.payments?.[0]?.pivot?.id}
												disabled={detail?.reported || detail?.reporting}
											/>
										) : (
											<div className="text-center mt-4">
												<span>Nessun pagamento associato a questo elemento</span>
											</div>
										)}
									</Tab>
								)}
							</Tabs>
						)}
					</Modal.Body>
				</Modal>
			)}
		</div>
	);
};
export default InvoiceDetailSummaryModal;
